"use client";

import { CheckCircle2, Circle, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/i18n/navigation";

interface Step {
  id: string;
  title: string;
  description: string;
  status: "done" | "current" | "pending";
  icon: React.ReactNode;
  ctaText?: string;
  ctaHref?: string;
}

interface ProgressTrackerProps {
  steps: Step[];
}

export function ProgressTracker({ steps }: ProgressTrackerProps) {
  return (
    <div className="space-y-6">
      {steps.map((step, index) => (
        <div key={step.id} className="flex gap-6">
          {/* Icon & connector */}
          <div className="flex flex-col items-center gap-2">
            <div
              className={`
                w-10 h-10 rounded-full flex items-center justify-center
                ${
                  step.status === "done"
                    ? "bg-green-100 text-green-600"
                    : step.status === "current"
                      ? "bg-purple-100 text-purple-600"
                      : "bg-gray-100 text-gray-400"
                }
              `}
            >
              {step.status === "done" ? (
                <CheckCircle2 className="w-6 h-6" />
              ) : step.status === "current" ? (
                <div className="w-3 h-3 bg-purple-600 rounded-full" />
              ) : (
                <Circle className="w-5 h-5" />
              )}
            </div>
            {index < steps.length - 1 && (
              <div className="w-1 h-12 bg-gradient-to-b from-gray-200 to-gray-100" />
            )}
          </div>

          {/* Content */}
          <div className="flex-1 pb-2">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3
                  className={`
                    text-lg font-semibold
                    ${
                      step.status === "done"
                        ? "text-green-600"
                        : step.status === "current"
                          ? "text-purple-600"
                          : "text-gray-400"
                    }
                  `}
                >
                  {step.status === "done" && "✓ "}
                  {step.status === "current" && "→ "}
                  {step.status === "pending" && "○ "}
                  {step.title}
                </h3>
                <p className="text-sm text-gray-600 mt-1">{step.description}</p>
              </div>
            </div>

            {/* CTA Button */}
            {step.ctaHref && step.ctaText && (
              <div className="mt-3">
                <Link href={step.ctaHref}>
                  <Button
                    variant={
                      step.status === "current" ? "default" : "outline"
                    }
                    size="sm"
                    className={`
                      ${
                        step.status === "current"
                          ? "bg-purple-600 hover:bg-purple-700"
                          : step.status === "done"
                            ? "bg-green-100 text-green-700 hover:bg-green-200"
                            : "opacity-50 cursor-not-allowed"
                      }
                    `}
                    disabled={step.status === "pending"}
                  >
                    {step.ctaText}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
